import { supabase } from '../lib/supabase';

export interface BookingPhoto {
  id: string;
  booking_id: string;
  vehicle_id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface UploadBookingPhotoParams {
  bookingId: string;
  vehicleId: string;
  file: File;
}

export interface UploadBookingPhotoResult {
  success: boolean;
  photo?: BookingPhoto;
  error?: string;
}

/**
 * Upload a photo for a booking/vehicle
 */
export async function uploadBookingPhoto({
  bookingId,
  vehicleId,
  file
}: UploadBookingPhotoParams): Promise<UploadBookingPhotoResult> {
  try {
    console.log('📸 Starting booking photo upload:', { bookingId, vehicleId, fileName: file.name });

    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `booking-photos/${bookingId}/${vehicleId}/${fileName}`;

    console.log('📸 Generated file path:', filePath);

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('user-uploads') // Use the established user-uploads bucket
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('❌ Storage upload error:', uploadError);
      return { success: false, error: `Upload failed: ${uploadError.message}` };
    }

    console.log('✅ File uploaded to storage:', uploadData.path);

    // Save photo record to database
    console.log('📸 Inserting booking photo record:', { 
      bookingId, 
      vehicleId, 
      fileName: file.name 
    });

    const { data: photoData, error: dbError } = await supabase
      .from('booking_photos')
      .insert({
        booking_id: bookingId,
        vehicle_id: vehicleId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        mime_type: file.type
      })
      .select()
      .single();

    if (dbError) {
      console.error('❌ Database insert error:', dbError);
      
      // Clean up uploaded file if database insert fails
      await supabase.storage
        .from('inspection-photos')
        .remove([filePath]);
      
      return { success: false, error: `Database error: ${dbError.message}` };
    }

    console.log('✅ Booking photo record saved to database:', photoData.id);

    return { success: true, photo: photoData };
  } catch (error) {
    console.error('❌ Unexpected error uploading booking photo:', error);
    return { success: false, error: 'Unexpected error occurred' };
  }
}

/**
 * Get photos for a specific booking/vehicle
 */
export async function getBookingPhotos(
  bookingId: string,
  vehicleId: string
): Promise<BookingPhoto[]> {
  try {
    if (!bookingId || !vehicleId || vehicleId === 'null') {
      return [];
    }

    console.log('📸 Fetching photos for booking/vehicle:', { bookingId, vehicleId });

    const { data, error } = await supabase
      .from('booking_photos')
      .select('*')
      .eq('booking_id', bookingId)
      .eq('vehicle_id', vehicleId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error fetching booking photos:', error);
      return [];
    }

    console.log('✅ Found booking photos:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('❌ Unexpected error fetching booking photos:', error);
    return [];
  }
}

/**
 * Get all photos for a booking
 */
export async function getAllBookingPhotos(bookingId: string): Promise<BookingPhoto[]> {
  try {
    const { data, error } = await supabase
      .from('booking_photos')
      .select('*')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error fetching all booking photos:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('❌ Unexpected error fetching all booking photos:', error);
    return [];
  }
}

/**
 * Delete a booking photo
 */
export async function deleteBookingPhoto(photoId: string): Promise<boolean> {
  try {
    console.log('📸 Deleting booking photo:', photoId);

    // First get the photo record to get the file path
    const { data: photo, error: fetchError } = await supabase
      .from('booking_photos')
      .select('file_path')
      .eq('id', photoId)
      .single();

    if (fetchError || !photo) {
      console.error('❌ Error fetching booking photo for deletion:', fetchError);
      return false;
    }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('user-uploads')
        .remove([photo.file_path]);

    if (storageError) {
      console.error('❌ Error deleting from storage:', storageError);
      // Continue with database deletion even if storage deletion fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('booking_photos')
      .delete()
      .eq('id', photoId);

    if (dbError) {
      console.error('❌ Error deleting from database:', dbError);
      return false;
    }

    console.log('✅ Booking photo deleted successfully');
    return true;
  } catch (error) {
    console.error('❌ Unexpected error deleting booking photo:', error);
    return false;
  }
}

/**
 * Get public URL for a booking photo
 */
export function getBookingPhotoUrl(filePath: string): string {
  const { data } = supabase.storage
    .from('user-uploads')
    .getPublicUrl(filePath);
  
  return data.publicUrl;
}

/**
 * Convert blob URL to File object for upload
 */
export async function blobUrlToFile(blobUrl: string, fileName: string): Promise<File> {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], fileName, { type: blob.type });
}
